import React, { useCallback, useMemo } from 'react';
import { PauseIcon, PlayIcon, XIcon, WifiIcon, ZapIcon, ActivityIcon, Shield } from "lucide-react";
import { useFileTransfer, useMobyNodeClient, useMobyDB } from "../provider";
import { FileTransferStateEnum, type FileTransferRecord } from "@/lib/file-transfer-types";
import { useLiveQuery } from 'dexie-react-hooks';
import { getExtensionState, subscribeToExtensionState, type ExtensionState } from "@/lib/extension-state-service";
import { extensionCapabilityService, TRANSFER_THRESHOLDS } from "@/services";
import { MerkleReceiptBadge } from '../merkle-receipt';

// Helper function to get the state color for UI display
export function getStateColor(state: FileTransferStateEnum, isBackground: boolean): string {
  // Colors for background and text
  const stateColors = {
    [FileTransferStateEnum.IDLE]: { bg: 'bg-surface-container', text: 'text-on-surface' },
    [FileTransferStateEnum.PENDING_CONSENT]: { bg: 'bg-tertiary-container', text: 'text-on-tertiary-container' },
    [FileTransferStateEnum.PREPARING]: { bg: 'bg-secondary-container', text: 'text-on-secondary-container' },
    [FileTransferStateEnum.METADATA_PENDING]: { bg: 'bg-secondary-container', text: 'text-on-secondary-container' },
    [FileTransferStateEnum.METADATA_SENT]: { bg: 'bg-secondary-container', text: 'text-on-secondary-container' },
    [FileTransferStateEnum.METADATA_RECEIVED]: { bg: 'bg-secondary-container', text: 'text-on-secondary-container' },
    [FileTransferStateEnum.READY_FOR_TRANSFER]: { bg: 'bg-primary-container', text: 'text-on-primary-container' },
    [FileTransferStateEnum.TRANSFERRING]: { bg: 'bg-primary-container', text: 'text-on-primary-container' },
    [FileTransferStateEnum.PAUSED]: { bg: 'bg-secondary-container', text: 'text-on-secondary-container' },
    [FileTransferStateEnum.FINALIZING]: { bg: 'bg-primary-container', text: 'text-on-primary-container' },
    [FileTransferStateEnum.COMPLETED]: { bg: 'bg-tertiary-container', text: 'text-on-tertiary-container' },
    [FileTransferStateEnum.FAILED]: { bg: 'bg-error-container', text: 'text-on-error-container' },
    [FileTransferStateEnum.CANCELLED]: { bg: 'bg-surface-container', text: 'text-on-surface' },
    [FileTransferStateEnum.ERROR]: { bg: 'bg-error-container', text: 'text-on-error-container' },
  };
  
  return isBackground ? stateColors[state].bg : stateColors[state].text;
}

// Helper function to get a human-readable state label
export function getStateLabel(state: FileTransferStateEnum): string {
  const stateDisplayText = {
    [FileTransferStateEnum.IDLE]: 'Idle',
    [FileTransferStateEnum.PENDING_CONSENT]: 'Awaiting Consent',
    [FileTransferStateEnum.PREPARING]: 'Preparing',
    [FileTransferStateEnum.METADATA_PENDING]: 'Preparing',
    [FileTransferStateEnum.METADATA_SENT]: 'Metadata Sent',
    [FileTransferStateEnum.METADATA_RECEIVED]: 'Ready',
    [FileTransferStateEnum.READY_FOR_TRANSFER]: 'Ready',
    [FileTransferStateEnum.TRANSFERRING]: 'Transferring',
    [FileTransferStateEnum.PAUSED]: 'Paused',
    [FileTransferStateEnum.FINALIZING]: 'Finalizing',
    [FileTransferStateEnum.COMPLETED]: 'Completed',
    [FileTransferStateEnum.FAILED]: 'Failed',
    [FileTransferStateEnum.CANCELLED]: 'Cancelled',
    [FileTransferStateEnum.ERROR]: 'Error',
  };
  
  return stateDisplayText[state];
}

/**
 * Aggregate file transfers from ALL connected peers with peer-aware control methods
 * 
 * CRITICAL FIX: This bypasses the nested FileTransferProvider context issue
 * where only the innermost peer's transfers are visible. Instead, it directly
 * queries all peers from MobyNode to aggregate transfers globally.
 * 
 * Provides peer-aware control methods that automatically route actions to the
 * correct peer based on sessionId lookup.
 * 
 * Use this for:
 * - File transfer drawer (global view of all transfers)
 * - Transfer statistics across all peers
 * 
 * DO NOT use this for:
 * - Chat panel attachment cards (use contact-specific context)
 */
export function useAllPeersFileTransfers() {
  const node = useMobyNodeClient();
  const db = useMobyDB();
  const [aggregatedState, setAggregatedState] = React.useState<{
    activeTransfers: Map<string, FileTransferRecord>;
    sendQueue: Map<string, FileTransferRecord>;
    receiveQueue: Map<string, FileTransferRecord>;
    completedTransfers: Map<string, FileTransferRecord>; // Completed transfers (100%)
  }>({
    activeTransfers: new Map(),
    sendQueue: new Map(),
    receiveQueue: new Map(),
    completedTransfers: new Map()
  });
  
  // Track sessionId -> peer mapping for routing control actions
  const sessionToPeerMap = React.useRef<Map<string, any>>(new Map());
  
  // PERF FIX: Fingerprint to skip setAggregatedState when data hasn't changed.
  // Without this, every 5s poll creates new Map objects → React re-renders ALL consumers
  // (including every ChatMessageCard with attachments) → cascading Suspense flashes.
  const stateFingerprintRef = React.useRef('');
  // Track which DB-fallback sessionIds we've already logged to reduce console noise
  const loggedDbFallbacksRef = React.useRef<Set<string>>(new Set());
  // PERF FIX: Cache known duplicate DB sessionIds to skip expensive O(N*M) dedup scan
  // on subsequent poll cycles. Cleared when dbTransfers changes (new ref from useLiveQuery).
  const knownDuplicatesRef = React.useRef<Set<string>>(new Set());
  
  // DB-BACKED RECOVERY: Query fileTransfers table as source of truth for persistence
  // This ensures drawer shows transfers even if in-memory maps are lost after sleep/restart
  const dbTransfers = useLiveQuery(
    async () => {
      if (!db) return [];
      try {
        return await db.fileTransfers.toArray();
      } catch (err) {
        console.warn('[TRANSFER-SYNC] Failed to query DB fileTransfers:', err);
        return [];
      }
    },
    [db],
    [] // Default to empty array
  );
  
  // PERF FIX: Store dbTransfers in a stable ref so aggregateAllPeers() can read it
  // without dbTransfers being a useEffect dependency (which caused 198 remounts per transfer
  // because every flushDexieProgress DB write → useLiveQuery new array ref → useEffect re-run)
  const dbTransfersRef = React.useRef(dbTransfers);
  React.useEffect(() => {
    dbTransfersRef.current = dbTransfers;
  }, [dbTransfers]);

  // IDLE-CPU FIX: Whether the most recent aggregation found ANY active or
  // queued transfer. The adaptive sync timer reads this to switch between a
  // 5s active cadence and a 30s idle cadence — without this, the timer
  // walked every peer and DB transfer every 5s on a quiescent node
  // (typical idle case after a fresh login). The fingerprint guard inside
  // aggregateAllPeers prevented React re-renders, but the iteration itself
  // still ran. Counted-state mutations (completedTransfers stays populated
  // after a transfer ends) intentionally do NOT keep the fast cadence —
  // completed transfers don't change unless the user deletes them, and
  // those events are handled by the explicit listeners below.
  const hasActiveOrQueuedRef = React.useRef(false);
  
  React.useEffect(() => {
    const aggregateAllPeers = () => {
      const allActiveTransfers = new Map<string, FileTransferRecord>();
      const allSendQueue = new Map<string, FileTransferRecord>();
      const allReceiveQueue = new Map<string, FileTransferRecord>();
      const allCompletedTransfers = new Map<string, FileTransferRecord>(); // COMPLETED: Persist until user deletes
      const newSessionToPeerMap = new Map<string, any>();
      
      // 🔧 FIX: NULL SAFETY - Check if node.peers exists before iterating
      // This prevents blank screen crash when drawer is opened before peers are initialized
      // (e.g., after page load, during reconnection, or after Provider remount)
      if (!node || !node.peers) {
        console.warn('[TRANSFER-SYNC] Node or peers not initialized yet, returning empty state');
        setAggregatedState({
          activeTransfers: allActiveTransfers,
          sendQueue: allSendQueue,
          receiveQueue: allReceiveQueue,
          completedTransfers: allCompletedTransfers
        });
        return;
      }
      
      // Aggregate from all connected peers
      for (const peer of node.peers.values()) {
        // Get transfers from each peer (enriched with cached merkle progress)
        const peerActiveTransfers = peer.getActiveTransfers();
        const peerSendQueue = peer.getSendQueue();
        const peerReceiveQueue = peer.getReceiveQueue();
        const peerCompletedTransfers = peer.getCompletedTransfers(); // NEW: Get completed transfers
        
        // Merge into aggregated maps and track peer ownership
        // CRITICAL: Keep ALL transfers in the map for drawer visibility
        // The UI will filter counts separately - completed transfers must stay visible until user deletes
        peerActiveTransfers.forEach((transfer, sessionId) => {
          // Add ALL transfers to the map (including completed)
          // This ensures file drawer shows completed transfers with resume/cancel options
          allActiveTransfers.set(sessionId, transfer);
          // Always track peer mapping for control operations
          newSessionToPeerMap.set(sessionId, peer);
        });
        peerSendQueue.forEach((transfer, sessionId) => {
          allSendQueue.set(sessionId, transfer);
          newSessionToPeerMap.set(sessionId, peer);
        });
        peerReceiveQueue.forEach((transfer, sessionId) => {
          allReceiveQueue.set(sessionId, transfer);
          newSessionToPeerMap.set(sessionId, peer);
        });
        peerCompletedTransfers.forEach((transfer, sessionId) => {
          allCompletedTransfers.set(sessionId, transfer); // NEW: Track completed transfers
          newSessionToPeerMap.set(sessionId, peer);
        });
      }
      
      // FIX F1: Include node-level groupTransfers (canonical source for group file transfers).
      // Per-peer maps may have stale or missing entries — node.groupTransfers is the single
      // source of truth after the F1 migration. Route to the appropriate aggregated map by state.
      if (node.groupTransfers) {
        node.groupTransfers.forEach((transfer, sessionId) => {
          // Skip if already tracked by per-peer aggregation (per-peer takes precedence for P2P)
          if (allActiveTransfers.has(sessionId) || allSendQueue.has(sessionId) ||
              allReceiveQueue.has(sessionId) || allCompletedTransfers.has(sessionId)) {
            return;
          }
          const state = transfer.state;
          if (state === FileTransferStateEnum.COMPLETED) {
            allCompletedTransfers.set(sessionId, transfer);
          } else if (state === FileTransferStateEnum.TRANSFERRING || 
                     state === FileTransferStateEnum.READY_FOR_TRANSFER ||
                     state === FileTransferStateEnum.PREPARING ||
                     state === FileTransferStateEnum.METADATA_RECEIVED) {
            allActiveTransfers.set(sessionId, transfer);
          } else if (transfer.metadata?.direction === 'outbound') {
            allSendQueue.set(sessionId, transfer);
          } else {
            allReceiveQueue.set(sessionId, transfer);
          }
          // Map to any connected peer for control routing (group transfers aren't peer-specific)
          if (!newSessionToPeerMap.has(sessionId)) {
            for (const peer of node.peers.values()) {
              if ((peer as any).chat?.channel?.readyState === 'open') {
                newSessionToPeerMap.set(sessionId, peer);
                break;
              }
            }
          }
        });
      }
      
      // STARTUP RECOVERY: Include node-level globalCompletedTransfers
      // These are completed transfers recovered from OPFS during startup (before peer connects)
      // This ensures completed transfers show in UI even when peer is offline
      if (node.globalCompletedTransfers) {
        node.globalCompletedTransfers.forEach((transfer, sessionId) => {
          // Only add if not already tracked by a peer (peer-level takes precedence)
          if (!allCompletedTransfers.has(sessionId)) {
            allCompletedTransfers.set(sessionId, transfer);
            // No peer mapping for global transfers - control operations will need to find peer dynamically
          }
        });
      }
      
      // DB-BACKED FALLBACK: Include transfers from DexieDB fileTransfers table
      // This ensures drawer shows transfers that exist as attachment cards in chat
      // even if in-memory maps were lost after sleep/restart (OPFS recovery failed)
      // PERF FIX: Read from ref (not dependency) to avoid useEffect remount loop
      const currentDbTransfers = dbTransfersRef.current;
      if (currentDbTransfers && currentDbTransfers.length > 0) {
        // PERF FIX: Build a Set of rootHash:direction keys from in-memory maps ONCE.
        // Previous code spread all 4 Maps into arrays and did .find() for EACH DB entry
        // — O(N*M) per poll cycle, dominating main thread during OPFS writes (60+ hits/cycle at 67%+).
        const inMemoryRootKeys = new Set<string>();
        for (const maps of [allActiveTransfers, allSendQueue, allReceiveQueue, allCompletedTransfers]) {
          for (const t of maps.values()) {
            const tRoot = t.rootHash || t.metadata?.rootHash;
            const tDir = t.metadata?.direction;
            if (tRoot) {
              inMemoryRootKeys.add(`${tRoot}:${tDir || 'any'}`);
              if (!tDir) inMemoryRootKeys.add(`${tRoot}:any`); // Match any direction
            }
          }
        }
        
        for (const dbTransfer of currentDbTransfers) {
          // Use sessionId if available, otherwise use uuid as key
          const sessionId = dbTransfer.sessionId || dbTransfer.uuid;
          
          // PERF FIX: Skip known duplicates instantly (cached from previous polls)
          if (knownDuplicatesRef.current.has(sessionId)) continue;
          
          // Skip if already tracked by in-memory maps (they have richer data)
          if (allActiveTransfers.has(sessionId) || 
              allSendQueue.has(sessionId) || 
              allReceiveQueue.has(sessionId) || 
              allCompletedTransfers.has(sessionId)) {
            continue;
          }
          
          // Convert FileTransferEntity to FileTransferRecord
          // Parse resumeData for additional metadata if available
          let resumeData: any = {};
          if (dbTransfer.resumeData) {
            try {
              resumeData = JSON.parse(dbTransfer.resumeData);
            } catch { /* ignore parse errors */ }
          }
          
          // CONTEXT FIX: Extract sender/receiver addresses for "From/To" display
          // Priority: resumeData > dbTransfer direct fields
          const senderAddress = resumeData.senderAddress || dbTransfer.senderAddress;
          const recipientAddress = resumeData.recipientAddress || dbTransfer.recipientAddress;
          const rootHash = dbTransfer.root || dbTransfer.hash;
          
          // DEDUP FIX: Skip if we already have a transfer with the same rootHash AND direction.
          // This prevents duplicate entries when same file has multiple session IDs,
          // but still allows the same file sent in both directions to coexist (Bug 3 fix).
          // PERF FIX: O(1) Set lookup instead of O(N) array spread + .find()
          const dbDirection = dbTransfer.dir === 'up' ? 'outbound' : 'inbound';
          const isDuplicate = inMemoryRootKeys.has(`${rootHash}:${dbDirection}`) ||
                              inMemoryRootKeys.has(`${rootHash}:any`);
          if (isDuplicate) {
            // Cache this duplicate so future polls skip it instantly
            knownDuplicatesRef.current.add(sessionId);
            console.debug('[TRANSFER-SYNC] Skipping DB duplicate (same rootHash+direction):', {
              sessionId: sessionId.slice(0, 8),
              rootHash: rootHash?.slice(0, 8),
              direction: dbDirection,
            });
            continue;
          }
          
          const transfer: FileTransferRecord = {
            rootHash: rootHash,
            sessionId: sessionId,
            state: dbTransfer.status === 'completed' ? FileTransferStateEnum.COMPLETED :
                   dbTransfer.status === 'paused' ? FileTransferStateEnum.PAUSED :
                   dbTransfer.status === 'cancelled' ? FileTransferStateEnum.CANCELLED :
                   FileTransferStateEnum.READY_FOR_TRANSFER,
            metadata: {
              name: resumeData.fileName || dbTransfer.fileName || `File ${rootHash?.slice(0, 8) || 'unknown'}`,
              size: resumeData.fileSize || dbTransfer.fileSize || dbTransfer.bytesTransferred || 0,
              rootHash: rootHash,
              chunkSize: resumeData.chunkSize || 65536,
              totalChunks: resumeData.totalChunks || 0,
              peerAddress: dbTransfer.peerAddress,
              senderAddress: senderAddress,       // FIX: Preserve "From:" context
              recipientAddress: recipientAddress, // FIX: Preserve "To:" context
              direction: dbTransfer.dir === 'up' ? 'outbound' : 'inbound',
              state: dbTransfer.status === 'completed' ? FileTransferStateEnum.COMPLETED :
                     FileTransferStateEnum.READY_FOR_TRANSFER,
              timestamp: dbTransfer.createdAt || Date.now(),
              vectorClock: new Map(),
              completedChunks: dbTransfer.progress ? Math.round((dbTransfer.progress / 100) * (resumeData.totalChunks || 0)) : 0,
              // ATTACHMENT_HASH_BRIDGE: Restore from resumeData for AttachmentCard lookup after recovery
              attachmentHash: resumeData.attachmentHash || undefined,
            },
            chunks: new Map(),
            completedChunks: new Set(),
            vectorClock: new Map(),
            lastUpdated: dbTransfer.updatedAt || Date.now(),
          };
          
          // Route to appropriate map based on status
          if (dbTransfer.status === 'completed') {
            allCompletedTransfers.set(sessionId, transfer);
          } else if (dbTransfer.dir === 'up') {
            allSendQueue.set(sessionId, transfer);
          } else {
            allReceiveQueue.set(sessionId, transfer);
          }
          
          // B4 FIX: Populate sessionToPeerMap for DB-backed transfers so resume/cancel
          // can route correctly. DB fallback sessions are not in any peer's in-memory
          // map (e.g. PREPARING placeholders skipped by recoverTransfersForPeer, or
          // post-reboot transfers before OPFS recovery completes) — without this mapping
          // every resume attempt fails with "Cannot resume transfer: no peer found".
          // Strategy: match on peerAddress first; fall back to any open-chat peer.
          if (!newSessionToPeerMap.has(sessionId) && dbTransfer.peerAddress) {
            const exactPeer = node.peers.get(dbTransfer.peerAddress);
            if (exactPeer) {
              newSessionToPeerMap.set(sessionId, exactPeer);
            } else {
              for (const peer of node.peers.values()) {
                if ((peer as any).chat?.channel?.readyState === 'open') {
                  newSessionToPeerMap.set(sessionId, peer);
                  break;
                }
              }
            }
          }
          
          // DIAG: Log DB recovery additions ONCE per sessionId to avoid log spam.
          // Previously logged on every 5s poll cycle, creating thousands of warnings.
          if (!loggedDbFallbacksRef.current.has(sessionId)) {
            loggedDbFallbacksRef.current.add(sessionId);
            console.log('[TRANSFER-SYNC] DB fallback added transfer:', {
              sessionId: sessionId.slice(0, 8),
              rootHash: rootHash?.slice(0, 8) || '(empty)',
              status: dbTransfer.status,
              direction: dbTransfer.dir,
              routed: dbTransfer.status === 'completed' ? 'completed' : dbTransfer.dir === 'up' ? 'sendQueue' : 'receiveQueue',
            });
          }
        }
      }
      
      sessionToPeerMap.current = newSessionToPeerMap;
      
      // IDLE-CPU FIX: Update the adaptive-cadence flag BEFORE the fingerprint
      // short-circuit. The poll loop reads this on every tick to decide
      // whether to schedule the next aggregation in 5s (active) or 30s
      // (idle), and it must reflect the latest aggregation result even
      // when the snapshot identity didn't change.
      hasActiveOrQueuedRef.current =
        allActiveTransfers.size > 0 ||
        allSendQueue.size > 0 ||
        allReceiveQueue.size > 0;

      // PERF FIX: Build a fingerprint from session IDs + states.
      // Only call setAggregatedState if something actually changed.
      // This prevents creating new Map references that trigger re-renders
      // across all consumers (ChatMessageCard, FileTransferDrawer, etc.).
      // Include chunk progress for active/queued transfers so progress bars update.
      // Completed transfers only need state (they never change).
      const fingerprint = [
        ...[...allActiveTransfers.entries()].map(([k, v]) => `a:${k}:${v.state}:${v.metadata?.completedChunks ?? 0}`),
        ...[...allSendQueue.entries()].map(([k, v]) => `s:${k}:${v.state}:${v.metadata?.completedChunks ?? 0}`),
        ...[...allReceiveQueue.entries()].map(([k, v]) => `r:${k}:${v.state}:${v.metadata?.completedChunks ?? 0}`),
        ...[...allCompletedTransfers.entries()].map(([k, v]) => `c:${k}:${v.state}`),
      ].sort().join(',');
      
      if (fingerprint === stateFingerprintRef.current) {
        return; // No change — skip state update to avoid unnecessary re-renders
      }
      stateFingerprintRef.current = fingerprint;
      
      setAggregatedState({
        activeTransfers: allActiveTransfers,
        sendQueue: allSendQueue,
        receiveQueue: allReceiveQueue,
        completedTransfers: allCompletedTransfers
      });
    };
    
    // FIX: Call immediately on mount to catch already-recovered transfers
    // This ensures the drawer shows transfers that were recovered before it opened
    console.log('[TRANSFER-SYNC] Initial aggregation on mount');
    aggregateAllPeers();
    
    // IDLE-CPU FIX: Adaptive cadence — 5s while any transfer is active or
    // queued, 30s while completely idle. Replaces the old fixed 5s
    // setInterval which iterated every peer's transfer maps + every DB
    // fileTransfers row 12 times per minute even on a freshly-logged-in
    // node with zero in-flight work.
    //
    // The transfer event listeners below (queued/progress/completed/etc)
    // fire aggregateAllPeers immediately on real activity, so the slower
    // idle cadence is purely a backstop for the rare "state drift without
    // an event" case (e.g. a peer disconnect that prunes a stale queue
    // entry without firing a moby:transfer-* event).
    //
    // Self-rescheduling setTimeout chain — `syncTimer` always holds the
    // currently-pending timer ID so cleanup can clear whichever one is
    // active. This pattern is what we use in MobyPeer.startStatsCollection
    // and PeerStateStore.scheduleNextTransferPoll for the same reason.
    const ACTIVE_POLL_MS = 5000;
    const IDLE_POLL_MS = 30000;
    let syncTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleNext = () => {
      const interval = hasActiveOrQueuedRef.current ? ACTIVE_POLL_MS : IDLE_POLL_MS;
      syncTimer = setTimeout(() => {
        aggregateAllPeers();
        scheduleNext();
      }, interval);
    };
    scheduleNext();
    
    // Phase 4: Listen for transfer recovery events to immediately update UI
    const handleTransferRecovery = (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log('[ATTACHMENT-RECOVERY] Transfer recovered, updating attachment cards:', customEvent.detail);
      aggregateAllPeers(); // Immediately refresh to show recovered transfer
    };
    
    // Listen for transfer cancellation events to immediately update UI
    // This prevents 5-second stale state window that causes duplicate entries and role confusion
    const handleTransferCancelled = (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log('[TRANSFER-SYNC] Transfer cancelled, updating UI immediately:', customEvent.detail);
      aggregateAllPeers(); // Immediately refresh to remove cancelled transfer
    };
    
    // PHASE 1: Listen for transfer queued events to immediately update UI
    // This syncs badge count and attachment cards instantly when file is added to queue
    const handleTransferQueued = (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log('[TRANSFER-SYNC] Transfer queued, updating UI immediately:', customEvent.detail);
      aggregateAllPeers(); // Immediately refresh badge + attachment card
    };
    
    // Throttled handler for preparation progress events
    // During merkle/OPFS prep, moby:transfer-progress fires every 500ms.
    // Without throttle, each event triggers aggregateAllPeers() across ALL peers
    // with full enrichment — massive CPU overhead during the most CPU-critical phase.
    // 2s throttle lets the 5s poll handle most updates; events just ensure responsiveness.
    let lastProgressAggregation = 0;
    const PROGRESS_THROTTLE_MS = 2000;
    const handleTransferProgress = (event: Event) => {
      const now = Date.now();
      if (now - lastProgressAggregation >= PROGRESS_THROTTLE_MS) {
        lastProgressAggregation = now;
        aggregateAllPeers();
      }
    };
    
    // Listen for transfer failed events (e.g., sender OPFS storage exceeded)
    // Updates file drawer to show FAILED state and removes upload card
    const handleTransferFailed = (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log('[TRANSFER-SYNC] Transfer failed, updating UI:', customEvent.detail);
      aggregateAllPeers(); // Refresh to show failed transfer in drawer
    };
    
    // Immediate UI refresh when tab becomes visible
    // Background tabs throttle setInterval to ≥1s (≥1min after 5min), causing stale UI.
    // When user returns to the tab, immediately refresh to show current transfer state.
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('[TRANSFER-SYNC] Tab visible — immediate UI refresh');
        aggregateAllPeers();
      }
    };
    
    // GROUP_UI_FIX: Listen for group chunk received events for real-time UI updates
    // Group transfers don't fire moby:transfer-progress during receiving, so without
    // this the UI only updates every 5s from the poll loop, causing stale progress display.
    let lastGroupProgressAggregation = 0;
    const GROUP_PROGRESS_THROTTLE_MS = 1000; // 1s throttle — more frequent than poll but not every chunk
    const handleGroupChunkReceived = (event: Event) => {
      const customEvent = event as CustomEvent;
      const now = Date.now();
      if (now - lastGroupProgressAggregation >= GROUP_PROGRESS_THROTTLE_MS) {
        lastGroupProgressAggregation = now;
        console.log('[TRANSFER-SYNC] Group chunk received, refreshing UI:', {
          sessionId: customEvent.detail?.sessionId?.slice(0, 8),
          chunkIndex: customEvent.detail?.chunkIndex,
          progress: `${customEvent.detail?.completedChunks}/${customEvent.detail?.totalChunks}`
        });
        aggregateAllPeers();
      }
    };

    // SERVER_UI_FIX: Listen for group receiver progress updates (sender-side) for real-time UI
    // When sending a group file, this shows which peers are actively receiving in real-time.
    let lastServerProgressAggregation = 0;
    const SERVER_PROGRESS_THROTTLE_MS = 1000;
    const handleGroupReceiverProgress = (event: Event) => {
      const customEvent = event as CustomEvent;
      const now = Date.now();
      if (now - lastServerProgressAggregation >= SERVER_PROGRESS_THROTTLE_MS) {
        lastServerProgressAggregation = now;
        console.log('[TRANSFER-SYNC] Group receiver progress update, refreshing UI:', {
          sessionId: customEvent.detail?.sessionId?.slice(0, 8),
          receiver: customEvent.detail?.receiverAddress?.slice(0, 8),
          ackedChunks: customEvent.detail?.ackedChunks,
          totalChunks: customEvent.detail?.totalChunks
        });
        aggregateAllPeers();
      }
    };

    window.addEventListener('moby:transfer-recovered', handleTransferRecovery);
    window.addEventListener('moby:transfer-cancelled', handleTransferCancelled);
    window.addEventListener('moby:transfer-queued', handleTransferQueued);
    window.addEventListener('moby:transfer-progress', handleTransferProgress);
    window.addEventListener('moby:transfer-failed', handleTransferFailed);
    window.addEventListener('moby:group-chunk-received', handleGroupChunkReceived);
    window.addEventListener('moby:group-receiver-progress', handleGroupReceiverProgress);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      // IDLE-CPU FIX: clearTimeout (not clearInterval) since we converted
      // to a self-rescheduling setTimeout chain. Guarded null-check because
      // the timer might not yet be scheduled if cleanup races mount.
      if (syncTimer !== null) clearTimeout(syncTimer);
      window.removeEventListener('moby:transfer-recovered', handleTransferRecovery);
      window.removeEventListener('moby:transfer-cancelled', handleTransferCancelled);
      window.removeEventListener('moby:transfer-queued', handleTransferQueued);
      window.removeEventListener('moby:transfer-progress', handleTransferProgress);
      window.removeEventListener('moby:transfer-failed', handleTransferFailed);
      window.removeEventListener('moby:group-chunk-received', handleGroupChunkReceived);
      window.removeEventListener('moby:group-receiver-progress', handleGroupReceiverProgress);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [node]); // PERF FIX: Removed dbTransfers dependency — read from ref instead
  // DB writes (flushDexieProgress every 5s) no longer trigger useEffect re-runs
  
  // Peer-aware control methods
  const startTransfer = React.useCallback(async (sessionId: string): Promise<boolean> => {
    const peer = sessionToPeerMap.current.get(sessionId);
    if (!peer) {
      console.warn(`[AGGREGATED_CONTROL] Cannot start transfer: no peer found for session ${sessionId}`);
      return false;
    }
    
    // FIX F1: Check node-level groupTransfers first (canonical for group transfers),
    // then fall back to per-peer maps for P2P transfers.
    const transfer = node.groupTransfers?.get(sessionId) ||
                     peer.getActiveTransfers().get(sessionId) || 
                     peer.getReceiveQueue().get(sessionId) ||
                     peer.getSendQueue().get(sessionId);
    if (!transfer) {
      console.warn(`[AGGREGATED_CONTROL] Cannot start transfer: no transfer found with session ID ${sessionId}`);
      return false;
    }
    
    // CRITICAL: Check if this is a GROUP transfer first
    const isGroupTransfer = !!(transfer.metadata.isGroupTransfer || transfer.metadata.groupAddress);
    const isSender = transfer.metadata.peerAddress === node.publicKey;
    
    console.log(`[AGGREGATED_CONTROL] Role detection for transfer start`, {
      sessionId: sessionId.slice(0, 8),
      isGroupTransfer,
      groupAddress: transfer.metadata.groupAddress?.slice(0, 8),
      peerAddress: transfer.metadata.peerAddress?.slice(0, 8),
      ourPublicKey: node.publicKey?.slice(0, 8),
      remotePeerAddress: peer.address?.slice(0, 8),
      isSender,
      method: isGroupTransfer 
        ? (isSender ? 'sendFileToGroup' : 'sendGroupFileReady')
        : (isSender ? 'sendTransferReadyForChunks' : 'acknowledgeTransferReadyForChunks')
    });
    
    // GROUP TRANSFER FORK
    if (isGroupTransfer) {
      if (isSender) {
        // Group sender - this shouldn't happen from UI since sender auto-starts
        console.warn(`[AGGREGATED_CONTROL] Sender trying to start group transfer from UI - this should auto-start`);
        return false;
      } else {
        // Group receiver - send GROUP_FILE_READY message
        if (!peer.sendGroupFileReady) {
          console.error(`[AGGREGATED_CONTROL] Peer does not have sendGroupFileReady method`);
          return false;
        }
        return peer.sendGroupFileReady(sessionId, transfer)
          .then(() => true)
          .catch((error: Error) => {
            console.error(`[AGGREGATED_CONTROL] Error sending GROUP_FILE_READY ${sessionId}:`, error);
            return false;
          });
      }
    }
    
    // P2P TRANSFER FORK (existing logic)
    if (isSender) {
      // P2P sender
      return peer.sendTransferReadyForChunks(sessionId, transfer)
        .then(() => true)
        .catch((error: Error) => {
          console.error(`[AGGREGATED_CONTROL] Error starting P2P transfer ${sessionId}:`, error);
          return false;
        });
    } else {
      // P2P receiver
      return peer.acknowledgeTransferReadyForChunks(sessionId, transfer)
        .then(() => true)
        .catch((error: Error) => {
          console.error(`[AGGREGATED_CONTROL] Error starting P2P transfer ${sessionId}:`, error);
          return false;
        });
    }
  }, [node]);
  
  const pauseTransfer = React.useCallback(async (sessionId: string): Promise<boolean> => {
    const peer = sessionToPeerMap.current.get(sessionId);
    if (!peer) {
      console.warn(`[AGGREGATED_CONTROL] Cannot pause transfer: no peer found for session ${sessionId}`);
      return false;
    }
    return peer.pauseTransfer(sessionId);
  }, []);
  
  const resumeTransfer = React.useCallback(async (sessionId: string): Promise<boolean> => {
    const peer = sessionToPeerMap.current.get(sessionId);
    if (!peer) {
      console.warn(`[AGGREGATED_CONTROL] Cannot resume transfer: no peer found for session ${sessionId}`);
      return false;
    }
    return peer.resumeTransfer(sessionId);
  }, []);
  
  const cancelTransfer = React.useCallback(async (sessionId: string): Promise<boolean> => {
    const peer = sessionToPeerMap.current.get(sessionId);
    if (!peer) {
      // LOCAL PURGE: No connected peer (DB-only entry, offline peer, PREPARING placeholder).
      // Delete from DB and OPFS directly so the drawer/attachment card clears immediately.
      console.log(`[AGGREGATED_CONTROL] No peer for session ${sessionId} — performing local purge`);
      try {
        const { getOriginPrivateDirectory } = await import('native-file-system-adapter');
        // Find rootHash from DB so we can delete the data file
        // FIX: fileTransfers uses 'uuid' as primary key (stores sessionId value)
        const dbRecord = await node.db.fileTransfers
          .where('uuid').equals(sessionId).first();
        const rootHash = dbRecord?.root || dbRecord?.hash;

        // Delete DB record
        await node.db.fileTransfers.where('uuid').equals(sessionId).delete();
        if (rootHash) {
          await node.db.fileTransfers.where('root').equals(rootHash).delete();
        }

        // Delete OPFS files
        const opfsRoot = await getOriginPrivateDirectory();
        await opfsRoot.removeEntry(`merkle_tree_${sessionId}`, { recursive: false }).catch(() => {});
        if (rootHash) {
          await opfsRoot.removeEntry(`${rootHash}.tmp`, { recursive: false }).catch(() => {});
          await opfsRoot.removeEntry(rootHash, { recursive: false }).catch(() => {});
        }

        // Fire cancelled event so the aggregation loop removes the entry from UI
        window.dispatchEvent(new CustomEvent('moby:transfer-cancelled', {
          detail: { sessionId, rootHash, cancelledBy: 'local' }
        }));
        return true;
      } catch (err) {
        console.warn(`[AGGREGATED_CONTROL] Local purge failed for ${sessionId}:`, err);
        return false;
      }
    }
    return peer.cancelTransfer(sessionId);
  }, [node]);
  
  return {
    ...aggregatedState,
    startTransfer,
    pauseTransfer,
    resumeTransfer,
    cancelTransfer,
    transferStats: {
      // Count only NON-TERMINAL transfers as "active" for display (but keep all in map for drawer)
      totalActive: Array.from(aggregatedState.activeTransfers.values())
        .filter(t => t.state !== 'completed' && t.state !== 'failed' && t.state !== 'cancelled').length,
      totalQueued: aggregatedState.sendQueue.size + aggregatedState.receiveQueue.size,
      activeCount: Array.from(aggregatedState.activeTransfers.values())
        .filter(t => t.state !== 'completed' && t.state !== 'failed' && t.state !== 'cancelled').length,
      queuedSendCount: aggregatedState.sendQueue.size,
      queuedReceiveCount: aggregatedState.receiveQueue.size,
      completedCount: aggregatedState.completedTransfers.size // NEW: Count of 100% complete transfers
    }
  };
}

/**
 * Safe file transfer hook that handles missing context gracefully
 * This wrapper provides graceful degradation when FileTransferContext is not available,
 * which may occur during onboarding or when no peers exist yet.
 */
export function useSafeFileTransfer() {
  try {
    return useFileTransfer();
  } catch (error) {
    // Provide a fallback implementation when context is not available
    const emptyMap = new Map();
    return {
      activeTransfers: emptyMap,
      queuedTransfers: {
        send: emptyMap,
        receive: emptyMap,
      },
      completedTransfers: emptyMap,
      transferStats: {
        totalActive: 0,
        totalQueued: 0,
        totalCompleted: 0,
        totalFailed: 0,
        totalPaused: 0,
      },
      pauseTransfer: () => Promise.resolve(false),
      resumeTransfer: () => Promise.resolve(false),
      cancelTransfer: () => Promise.resolve(false),
      startTransfer: () => Promise.resolve(false),
      uploadFile: async () => null,
      getProgress: () => 0,
      sendFile: async () => {
        console.warn('[SafeFileTransfer] sendFile called but FileTransferContext not available');
        return Promise.resolve('fallback-session-id');
      }
    };
  }
}

/**
 * Hook for accessing extension state across all UI components
 * 
 * Provides consistent extension detection, capabilities, and browser compatibility
 * information to all file transfer UI components.
 */
export function useExtensionState() {
  // Start with safe defaults - extension is OPTIONAL for P2P transfers
  const [extensionState, setExtensionState] = React.useState<ExtensionState>({
    hasExtension: false,
    isChromium: false,
    isEdge: false,
    capabilities: null,
    isInitialized: false,
  });
  
  // Only detect extension when component is mounted (non-blocking)
  React.useEffect(() => {
    // Lazy initialization - don't block render
    try {
      const state = getExtensionState();
      setExtensionState(state);
    } catch (error) {
      console.warn('[ExtensionState] Detection failed, using defaults:', error);
      // Keep defaults - P2P works without extension
    }
    
    const unsubscribe = subscribeToExtensionState((state) => {
      setExtensionState(state);
    });
    
    return unsubscribe;
  }, []);
  
  return extensionState;
}

/**
 * Enhanced safe hook combining file transfer functionality with extension awareness
 * 
 * This provides all file transfer functionality plus extension capabilities in one hook,
 * making it easy for UI components to access both systems consistently.
 */
export function useSafeFileTransferWithExtension() {
  const fileTransfer = useSafeFileTransfer();
  const extensionState = useExtensionState();
  const [opfsLimit, setOpfsLimit] = React.useState<number | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const thresholds = await extensionCapabilityService.getDynamicThresholds();
        if (!cancelled) setOpfsLimit(thresholds.OPFS_LIMIT);
      } catch (e) {
        // keep null; callers can handle undefined state gracefully
      }
    })();
    return () => { cancelled = true; };
  }, []);
  
  return {
    ...fileTransfer,
    extension: extensionState,
    // Convenience properties for common checks
    hasExtension: extensionState.hasExtension,
    isChromium: extensionState.isChromium,
    isEdge: extensionState.isEdge,
    capabilities: extensionState.capabilities,
    // Helper functions
    isBrowserSupported: extensionState.isChromium || extensionState.isEdge,
    isExtensionFullyFunctional: extensionState.hasExtension && extensionState.capabilities !== null,
    getMaxFileSize: () => {
      if (extensionState.hasExtension && extensionState.capabilities) {
        return extensionState.capabilities.maxFileSize;
      }
      return opfsLimit ?? TRANSFER_THRESHOLDS.OPFS_LIMIT; // Dynamic OPFS fallback
    }
  };
}

/**
 * Circular progress indicator component
 * Shows progress percentage with a visual circle indicator
 */
export function TransferProgressIndicator({ 
  progress, 
  size = 40 
}: { 
  progress: number;
  size?: number;
}) {
  const STROKE_WIDTH = 10;
  const radius = `${(100 - STROKE_WIDTH) / 2}%`;
  const strokeWidth = `${STROKE_WIDTH}%`;
  const strokeDashoffset = (1 - progress / 100) * 100;

  return (
    <div className="shrink-0 rounded-full" style={{ width: size, height: size }}>
      <svg
        viewBox="0 0 4800 4800"
        className="text-primary"
        style={
          {
            "--stroke-width": STROKE_WIDTH,
            "--progress": progress,
          } as React.CSSProperties
        }
      >
        {/* @ts-expect-error */}
        <g transform="rotate(-90)" transformOrigin="50% 50%">
          <circle
            fill="transparent"
            pathLength="100"
            cx="50%"
            cy="50%"
            strokeWidth={strokeWidth}
            r={radius}
            className="stroke-outline-variant"
            strokeDasharray="100"
          />
          <circle
            fill="transparent"
            pathLength="100"
            cx="50%"
            cy="50%"
            stroke="currentColor"
            r={radius}
            strokeDashoffset={strokeDashoffset}
            strokeWidth={strokeWidth}
            className="[transition:stroke-dashoffset_500ms_cubic-bezier(0,0,0.2,1)]"
            strokeDasharray="100"
          />
        </g>
        {/* @ts-expect-error */}
        <g transform="scale(100)" transformOrigin="50% 50%">
          <text
            x="50%"
            y="50%"
            textAnchor="middle"
            dominantBaseline="middle"
            fill="currentColor"
            className="text-label-sm text-on-surface-variant"
          >
            {progress}%
          </text>
        </g>
      </svg>
    </div>
  );
}

/**
 * Transfer state indicator badge
 * Shows current transfer state with appropriate color coding
 */
export function TransferStateBadge({ 
  state,
  hasMerkleReceipt = false,
  merkleVerified = false
}: { 
  state: FileTransferStateEnum;
  hasMerkleReceipt?: boolean;
  merkleVerified?: boolean;
}) {
  return (
    <div className="inline-flex items-center gap-2">
      <span 
        className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
        style={{
          backgroundColor: getStateColor(state, true),
          color: getStateColor(state, false)
        }}
      >
        {getStateLabel(state)}
      </span>
      {hasMerkleReceipt && state === FileTransferStateEnum.COMPLETED && (
        <MerkleReceiptBadge hasReceipt={true} verified={merkleVerified} />
      )}
    </div>
  );
}

/**
 * Transfer control buttons component
 * Shows appropriate control buttons based on current transfer state
 */
export function TransferControlButtons({
  state,
  sessionId,
  pausedBy,
  direction,
  onPause,
  onResume,
  onCancel,
  onStart,
  size = 16
}: {
  state: FileTransferStateEnum;
  sessionId: string;
  pausedBy?: 'local' | 'peer' | 'disconnect';
  direction?: 'inbound' | 'outbound';
  onPause?: () => Promise<void>;
  onResume?: () => Promise<void>;
  onCancel?: () => Promise<void>;
  onStart?: () => Promise<void>;
  size?: number;
}) {
  // FIX: Remove useCallback hooks to prevent React Error #185
  // This component is rendered inside .map() loops, so hooks cause variable counts
  // The parent already provides stable callbacks, so these wrappers are unnecessary
  
  const handlePause = () => {
    if (!sessionId) return;
    void onPause?.().catch(console.error);
  };

  const handleResume = () => {
    if (!sessionId) return;
    void onResume?.().catch(console.error);
  };

  const handleCancel = () => {
    if (!sessionId) return;
    void onCancel?.().catch(console.error);
  };

  const handleStart = () => {
    if (!sessionId) return;
    void onStart?.().catch(console.error);
  };

  return (
    <div className="flex gap-2">
      {/* Pause button - shown during active transfer */}
      {state === FileTransferStateEnum.TRANSFERRING && (
        <button 
          onClick={handlePause} 
          className="group relative grid shrink-0 place-items-center rounded-full p-2 hover:cursor-pointer" 
          title="Pause transfer"
        >
          <span
            aria-hidden
            className="group-hover:bg-on-surface-variant/8 group-data-pressed:bg-on-surface-variant/10 absolute inset-0 rounded-full transition"
          />
          <PauseIcon size={size} />
        </button>
      )}
      
      {/* Resume button - only shown when paused by local user */}
      {/* CRITICAL FIX: Don't show resume button if paused by peer - they must resume */}
      {state === FileTransferStateEnum.PAUSED && pausedBy !== 'peer' && (
        <button 
          onClick={handleResume} 
          className="group relative grid shrink-0 place-items-center rounded-full p-2 hover:cursor-pointer" 
          title="Resume transfer"
        >
          <span
            aria-hidden
            className="group-hover:bg-on-surface-variant/8 group-data-pressed:bg-on-surface-variant/10 absolute inset-0 rounded-full transition"
          />
          <PlayIcon size={size} />
        </button>
      )}
      
      {/* Waiting indicator when paused by peer */}
      {state === FileTransferStateEnum.PAUSED && pausedBy === 'peer' && (
        <div className="flex items-center gap-1 text-xs text-on-surface-variant px-2">
          <span>Waiting for peer to resume...</span>
        </div>
      )}
      
      {/* Start button - shown when ready to begin, ONLY for receiver (inbound) */}
      {/* Sender waits for receiver to initiate - receiver requests chunks */}
      {(state === FileTransferStateEnum.READY_FOR_TRANSFER || state === FileTransferStateEnum.METADATA_RECEIVED) && 
       direction === 'inbound' && (
        <button 
          onClick={handleStart} 
          className="group relative grid shrink-0 place-items-center rounded-full p-2 hover:cursor-pointer" 
          title="Start transfer"
        >
          <span
            aria-hidden
            className="group-hover:bg-on-surface-variant/8 group-data-pressed:bg-on-surface-variant/10 absolute inset-0 rounded-full transition"
          />
          <PlayIcon size={size} />
        </button>
      )}
      
      {/* Waiting indicator for sender - receiver must initiate */}
      {(state === FileTransferStateEnum.READY_FOR_TRANSFER || state === FileTransferStateEnum.METADATA_RECEIVED) && 
       direction === 'outbound' && (
        <div className="flex items-center gap-1 text-xs text-on-surface-variant px-2">
          <span>Waiting for receiver...</span>
        </div>
      )}
      
      {/* Cancel / Dismiss button — always visible so users can clear any entry */}
      {/* Active states: red cancel. Terminal states (completed/failed/cancelled): muted dismiss */}
      <button 
        onClick={handleCancel} 
        className={`group relative grid shrink-0 place-items-center rounded-full p-2 hover:cursor-pointer ${
          state === FileTransferStateEnum.COMPLETED ||
          state === FileTransferStateEnum.FAILED ||
          state === FileTransferStateEnum.CANCELLED
            ? 'text-on-surface-variant'
            : 'text-error'
        }`}
        title={
          state === FileTransferStateEnum.COMPLETED ? 'Dismiss (removes local copy)' :
          state === FileTransferStateEnum.FAILED || state === FileTransferStateEnum.CANCELLED ? 'Dismiss' :
          'Cancel transfer'
        }
      >
        <span
          aria-hidden
          className="group-hover:bg-on-surface-variant/8 group-data-pressed:bg-on-surface-variant/10 absolute inset-0 rounded-full transition"
        />
        <XIcon size={size} />
      </button>
    </div>
  );
}

/**
 * Simple Network Quality Icon component
 * Shows just the health icon for network quality
 */
export function NetworkQualityIcon({
  quality
}: {
  quality: 'poor' | 'fair' | 'good' | 'excellent' | 'unknown';
}) {
  const getQualityColor = (q: string) => {
    switch (q) {
      case 'excellent': return 'text-green-600';
      case 'good': return 'text-blue-600';
      case 'fair': return 'text-yellow-600';
      case 'poor': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };
  
  const getQualityIcon = (q: string) => {
    switch (q) {
      case 'excellent': return <ZapIcon size={14} />;
      case 'good': return <WifiIcon size={14} />;
      case 'fair': return <ActivityIcon size={14} />;
      case 'poor': return <Shield size={14} />;
      default: return <WifiIcon size={14} />;
    }
  };
  
  return (
    <span className={`flex items-center ${getQualityColor(quality)}`} title={`Network: ${quality}`}>
      {getQualityIcon(quality)}
    </span>
  );
}

/**
 * Network Quality Indicator component
 * Shows real-time network performance metrics with visual indicators
 */
export function NetworkQualityIndicator({
  quality,
  bandwidth,
  chunkSize,
  adaptiveMode = false
}: {
  quality?: 'poor' | 'fair' | 'good' | 'excellent' | 'unknown';
  bandwidth?: number; // in bytes per second
  chunkSize?: number; // in KB
  adaptiveMode?: boolean;
}) {
  if (!quality || quality === 'unknown') return null;
  
  const getQualityColor = (q: string) => {
    switch (q) {
      case 'excellent': return 'text-green-600';
      case 'good': return 'text-blue-600';
      case 'fair': return 'text-yellow-600';
      case 'poor': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };
  
  const getQualityIcon = (q: string) => {
    switch (q) {
      case 'excellent': return <ZapIcon size={12} />;
      case 'good': return <WifiIcon size={12} />;
      case 'fair': return <ActivityIcon size={12} />;
      case 'poor': return <WifiIcon size={12} />;
      default: return <WifiIcon size={12} />;
    }
  };
  
  return (
    <div className="flex items-center gap-1 text-xs">
      <span className={`flex items-center gap-1 ${getQualityColor(quality)}`}>
        {getQualityIcon(quality)}
        {quality}
      </span>
      {bandwidth && (
        <span className="text-on-secondary-container">
          · {(bandwidth / 1024 / 1024 * 8).toFixed(1)}Mbps
        </span>
      )}
      {chunkSize && adaptiveMode && (
        <span className="text-on-secondary-container">
          · {chunkSize}KB chunks
        </span>
      )}
      {adaptiveMode && (
        <span className="text-green-600 text-xs font-medium">
          ADAPTIVE
        </span>
      )}
    </div>
  );
}

/**
 * Enhanced Transfer info display component
 * Shows transfer rate, time remaining, network quality, and other metadata
 */
export function TransferInfoDisplay({
  transferRate,
  timeRemaining,
  direction,
  username,
  startedAt,
  networkQuality,
  bandwidth,
  chunkSize,
  adaptiveMode = false
}: {
  transferRate: string;
  timeRemaining: string;
  direction: "upload" | "download";
  username: string;
  startedAt: string;
  networkQuality?: 'poor' | 'fair' | 'good' | 'excellent' | 'unknown';
  bandwidth?: number;
  chunkSize?: number;
  adaptiveMode?: boolean;
}) {
  // Format time remaining to use ##/h ##/m ##/s format
  const formatTimeCompact = (timeStr: string) => {
    // Handle special cases first
    if (timeStr.toLowerCase().includes('calculating') || 
        timeStr.toLowerCase().includes('finishing') || 
        timeStr.toLowerCase().includes('starting') ||
        timeStr.toLowerCase().includes('connecting')) {
      return timeStr; // Return as-is for status messages
    }
    
    // Parse the time string (e.g., "2 hours 15 minutes 30 seconds")
    const hourMatch = timeStr.match(/(\d+)\s*hours?/i);
    const minuteMatch = timeStr.match(/(\d+)\s*minutes?/i);
    const secondMatch = timeStr.match(/(\d+)\s*seconds?/i);
    
    const parts = [];
    
    if (hourMatch && parseInt(hourMatch[1]) > 0) {
      parts.push(`${hourMatch[1]}/h`);
    }
    if (minuteMatch && parseInt(minuteMatch[1]) > 0) {
      parts.push(`${minuteMatch[1]}/m`);
    }
    if (secondMatch && parseInt(secondMatch[1]) > 0) {
      parts.push(`${secondMatch[1]}/s`);
    }
    
    // If no parts found, likely already formatted or a special case
    if (parts.length === 0) {
      return timeStr;
    }
    
    return parts.join(' ') + ' Remaining';
  };
  
  // Format bandwidth - it's already in bytes per second
  const formatBandwidth = (bw: number) => {
    // bw is in bytes per second (confirmed from provider.tsx line 289)
    const megabytesPerSec = bw / 1024 / 1024; // Convert bytes/s to MB/s
    
    // Use fixed width formatting to reduce horizontal bouncing
    if (megabytesPerSec >= 10) {
      return `${megabytesPerSec.toFixed(0)} MB/s`;  // No decimal for 10+ MB/s
    } else if (megabytesPerSec >= 1) {
      return `${megabytesPerSec.toFixed(1)} MB/s`;  // One decimal for 1-9.9 MB/s
    } else {
      const kbPerSec = megabytesPerSec * 1024;
      return `${kbPerSec.toFixed(0)} KB/s`;
    }
  };
  
  // Format chunk size - input is in bytes
  const formatChunkSize = (sizeInBytes: number) => {
    // Convert bytes to appropriate unit
    const sizeInKB = sizeInBytes / 1024;
    const sizeInMB = sizeInKB / 1024;
    
    if (sizeInMB >= 1) {
      // Show as MB for sizes >= 1MB
      return sizeInMB >= 10 ? `${sizeInMB.toFixed(0)}MB` : `${sizeInMB.toFixed(1)}MB`;
    } else {
      // Show as KB for sizes < 1MB
      return `${sizeInKB.toFixed(0)}KB`;
    }
  };
  
  return (
    <div className="text-label-sm space-y-1">
      {/* Metrics Line 1: Transfer Speed - Channel Bandwidth - Health Icon */}
      <div className="text-on-secondary-container flex items-center gap-2">
        <span>{transferRate}</span>
        {bandwidth && (
          <>
            <span>·</span>
            <span>{formatBandwidth(bandwidth)}</span>
          </>
        )}
        {networkQuality && networkQuality !== 'unknown' && (
          <>
            <span>·</span>
            <NetworkQualityIcon quality={networkQuality} />
          </>
        )}
      </div>
      
      {/* Metrics Line 2: Time Estimate - Chunk Size */}
      <div className="text-on-secondary-container flex items-center gap-2">
        <span>{formatTimeCompact(timeRemaining)}</span>
        {chunkSize && (
          <>
            <span>·</span>
            <span>{formatChunkSize(chunkSize)}</span>
          </>
        )}
        {adaptiveMode && (
          <>
            <span>·</span>
            <span className="text-green-600 text-xs font-medium">ADAPTIVE</span>
          </>
        )}
      </div>
      
      {/* User and timestamp info */}
      <div>
        {direction === "upload" && "To"}
        {direction === "download" && "From"}{" "}
        <span className="text-on-secondary-container">{username}</span> ·{" "}
        <span>{startedAt}</span>
      </div>
    </div>
  );
}

/**
 * Complete file transfer item component for use in chat cards
 * Combines all the above components into a compact chat-friendly layout
 */
export function ChatFileTransferItem({
  filename,
  size,
  progress,
  transferRate,
  timeRemaining,
  direction,
  startedAt,
  username,
  sessionId,
  state,
  onPause,
  onResume,
  onCancel,
  onStart,
  onDownload
}: {
  filename: string;
  size: string;
  progress: number;
  transferRate: string;
  timeRemaining: string;
  direction: "upload" | "download";
  startedAt: string;
  username: string;
  sessionId: string;
  state: FileTransferStateEnum;
  onPause?: () => Promise<void>;
  onResume?: () => Promise<void>;
  onCancel?: () => Promise<void>;
  onStart?: () => Promise<void>;
  onDownload?: () => Promise<void>;
}) {
  return (
    <div className="rounded-medium text-on-surface group-data-user:bg-surface-container-highest bg-surface-container-high mb-1 p-3">
      <div className="flex items-center gap-3">
        {/* Progress indicator */}
        <TransferProgressIndicator progress={progress} size={48} />
        
        {/* File info and controls */}
        <div className="flex-1 min-w-0">
          <div className="text-title-sm text-on-primary-container truncate">
            {filename} - {size}
          </div>
          
          {/* State badge */}
          <div className="flex items-center gap-2 mt-1">
            <TransferStateBadge state={state} />
          </div>
          
          {/* Transfer info */}
          {state === FileTransferStateEnum.TRANSFERRING && (
            <TransferInfoDisplay
              transferRate={transferRate}
              timeRemaining={timeRemaining}
              direction={direction}
              username={username}
              startedAt={startedAt}
            />
          )}
          
          {/* Control buttons */}
          <div className="mt-2">
            {state === FileTransferStateEnum.COMPLETED && onDownload ? (
              <button
                onClick={() => onDownload?.()}
                className="group relative grid shrink-0 place-items-center rounded-full p-2 hover:cursor-pointer"
                title="Download completed file"
              >
                <span
                  aria-hidden
                  className="group-hover:bg-on-surface-variant/8 group-data-pressed:bg-on-surface-variant/10 absolute inset-0 rounded-full transition"
                />
                <PlayIcon size={16} />
              </button>
            ) : (
              <TransferControlButtons
                state={state}
                sessionId={sessionId}
                direction={direction === 'upload' ? 'outbound' : 'inbound'}
                onPause={onPause}
                onResume={onResume}
                onCancel={onCancel}
                onStart={onStart}
                size={16}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
